import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Auth() {
  const navigate = useNavigate();
  const { authenticate } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !consent) {
      toast("Please agree to the Terms and Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const body =
        mode === "login"
          ? { email, password }
          : { email, password, name, consent };
      const { data } = await api.post(path, body);
      authenticate(data.user);
      if (mode === "register") {
        // New account: choose an appearance before onboarding.
        navigate("/theme");
      } else {
        navigate(data.user.onboarded ? "/begin" : "/onboarding");
      }
    } catch (err) {
      toast(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const continueWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/begin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl,
    )}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-8">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
        className="w-full max-w-sm"
      >
        <h1 className="font-serif-display font-light text-3xl sm:text-4xl tracking-tight text-center">
          {mode === "login" ? "Return to the mirror" : "Step to the mirror"}
        </h1>
        <p className="mt-3 text-center text-[var(--mirror-text-3)] text-sm">
          {mode === "login"
            ? "Continue where your reflection left off."
            : "There are no wrong answers, only starting points."}
        </p>

        <form onSubmit={submit} className="mt-12 space-y-8">
          {mode === "register" && (
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-3)]">
                Name (optional)
              </label>
              <input
                data-testid="auth-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-transparent border-b border-[rgba(242,236,228,0.15)] py-2 mt-2 outline-none focus:border-[var(--mirror-accent)] transition-colors duration-500 text-[var(--mirror-text)]"
              />
            </div>
          )}
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-3)]">
              Email
            </label>
            <input
              data-testid="auth-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-b border-[rgba(242,236,228,0.15)] py-2 mt-2 outline-none focus:border-[var(--mirror-accent)] transition-colors duration-500 text-[var(--mirror-text)]"
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-3)]">
              Password
            </label>
            <input
              data-testid="auth-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b border-[rgba(242,236,228,0.15)] py-2 mt-2 outline-none focus:border-[var(--mirror-accent)] transition-colors duration-500 text-[var(--mirror-text)]"
            />
            {mode === "login" && (
              <div className="mt-3 text-right">
                <Link
                  data-testid="auth-forgot-password-link"
                  to="/forgot-password"
                  className="text-xs text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500"
                >
                  Forgot password?
                </Link>
              </div>
            )}
          </div>

          {mode === "register" && (
            <>
              <p className="text-xs text-[var(--mirror-text-3)] leading-relaxed -mt-4">
                Use at least 10 characters, with an uppercase and lowercase letter, a number, and a
                special character.
              </p>
              <label
                data-testid="auth-consent-label"
                className="flex items-start gap-3 text-sm text-[var(--mirror-text-2)] cursor-pointer"
              >
                <input
                  data-testid="auth-consent-checkbox"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 h-4 w-4 shrink-0 accent-[var(--mirror-accent)]"
                />
                <span>
                  I agree to the{" "}
                  <Link to="/terms" className="underline underline-offset-2 hover:text-[var(--mirror-text)]">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="underline underline-offset-2 hover:text-[var(--mirror-text)]">
                    Privacy Policy
                  </Link>
                  , and understand Mirror provides AI reflection, not therapy.
                </span>
              </label>
            </>
          )}

          <button
            data-testid="auth-submit-button"
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-full border border-[rgba(242,236,228,0.2)] text-sm tracking-[0.2em] uppercase hover:bg-[rgba(242,236,228,0.06)] transition-colors duration-700 disabled:opacity-40"
          >
            {loading ? "…" : mode === "login" ? "Enter" : "Begin"}
          </button>
        </form>

        <div className="mt-8 flex items-center gap-4">
          <span className="flex-1 h-px bg-[rgba(242,236,228,0.12)]" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-[var(--mirror-text-3)]">
            or
          </span>
          <span className="flex-1 h-px bg-[rgba(242,236,228,0.12)]" />
        </div>

        <button
          data-testid="auth-google-button"
          type="button"
          onClick={continueWithGoogle}
          className="mt-8 w-full py-3 rounded-full border border-[rgba(242,236,228,0.2)] flex items-center justify-center gap-3 text-sm tracking-[0.15em] uppercase hover:bg-[rgba(242,236,228,0.06)] transition-colors duration-700"
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
            <path fill="#4CAF50" d="M24 44c5.5 0 10.4-2.1 14.1-5.5l-6.5-5.5c-2 1.5-4.7 2.5-7.6 2.5-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z" />
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.5l6.5 5.5C41.6 36 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z" />
          </svg>
          Continue with Google
        </button>

        <button
          data-testid="auth-toggle-mode"
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          className="mt-10 w-full text-center text-xs tracking-wide text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500"
        >
          {mode === "login"
            ? "No reflection yet? Create one."
            : "Already have a reflection? Sign in."}
        </button>
      </motion.div>
    </div>
  );
}
